import {
  patchState,
  signalStoreFeature,
  SignalStoreFeature,
  SignalStoreFeatureResult,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod, RxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, Observable, of, pipe, switchMap, tap } from 'rxjs';

import { pollUntil } from './poll-until';

export interface AsyncJobConfig<Input, Job> {
  /** Enqueues the job. Its response is treated as the job's first known state, so a
   *  backend that answers `202` with a body already terminal costs zero polls. */
  start: (input: Input) => Observable<Job>;
  /** Reads the state of an existing job. */
  poll: (jobId: string) => Observable<Job>;
  jobIdOf: (job: Job) => string;
  /** `true` → the job reached a terminal state. Kept as config so the feature never
   *  needs to know the backend's status vocabulary. */
  done: (job: Job) => boolean;
  delays: readonly [number, ...number[]];
  timeout: number;
  /** `sessionStorage` key for the in-flight job id. Omit → no persistence.
   *  Per-TAB on purpose: with `localStorage` two tabs would watch the same job and
   *  both announce it. */
  persistKey?: string;
}

interface AsyncJobState<Job> {
  /** Last known state of the job, terminal included — a `failed` job lives HERE, not
   *  in `jobError`: the request succeeded, the work did not. */
  job: Job | null;
  jobRunning: boolean;
  /**
   * Transport-level failure only: the thrown value AS IS, not a message.
   *
   * The raw error is what lets a consumer tell an HTTP failure (which an app-wide error
   * interceptor has almost certainly already reported) from a `PollTimeoutError` (which
   * nothing else knows about) and stay quiet for the first. Flattening it to a string
   * here would force every consumer to double-report or say nothing.
   */
  jobError: unknown;
}

/**
 * `signalStoreFeature` for the "backend enqueues a job, the frontend watches it" shape:
 * `startJob` → POST → poll until terminal, with every intermediate state exposed on
 * `job()` so a progress bar can read it.
 *
 * Lives in the STORE, not a component, so the watch survives closing a modal and
 * navigating; it dies with the store's injector, which is logout.
 *
 * With `persistKey` set, the in-flight id round-trips through `sessionStorage`, so
 * `resumeJob()` picks the job back up after an F5. A resumed job that is gone answers
 * `404` and is dropped SILENTLY — the user never asked for that request, so surfacing
 * an error on app boot would be noise.
 *
 * ponytail: one job per store, fixed slice names. Parameterising the slice prefix needs
 * template-literal types and buys nothing until a single store runs two jobs at once;
 * a second consuming STORE already works untouched.
 */
export function withAsyncJob<Input, Job>(
  configFactory: () => AsyncJobConfig<Input, Job>,
): SignalStoreFeature<
  // `SignalStoreFeatureResult` = "requires nothing of the consuming store", so this can
  // be plugged in at ANY position. Spelling the input as `methods: Record<string, never>`
  // (what `withCursorPagination` does, since it sits right after `withEntities`) would
  // reject every store that already declared a method.
  SignalStoreFeatureResult,
  {
    state: AsyncJobState<Job>;
    props: object;
    methods: {
      startJob: RxMethod<Input>;
      resumeJob: () => void;
      clearJob: () => void;
    };
  }
> {
  const initialState: AsyncJobState<Job> = {
    job: null,
    jobRunning: false,
    jobError: null,
  };

  return signalStoreFeature(
    withState<AsyncJobState<Job>>(initialState),
    withMethods((store) => {
      // Built inside withMethods (an injection context) so `start`/`poll` can inject.
      const config = configFactory();

      const persist = (jobId: string): void => {
        if (config.persistKey) sessionStorage.setItem(config.persistKey, jobId);
      };
      const forget = (): void => {
        if (config.persistKey) sessionStorage.removeItem(config.persistKey);
      };

      /**
       * Shared tail of both entry points: take the first known state, keep polling while
       * it is not terminal, and mirror every response onto the slice.
       * `silent` is what separates a resume (nobody asked) from a user-triggered export.
       */
      const watch = (first$: Observable<Job>, silent: boolean): Observable<Job> =>
        first$.pipe(
          switchMap((first) => {
            patchState(store, { job: first });
            if (config.done(first)) return of(first);
            persist(config.jobIdOf(first));
            return pollUntil({
              fetch: () => config.poll(config.jobIdOf(first)),
              done: config.done,
              delays: config.delays,
              timeout: config.timeout,
            });
          }),
          tap((job) => {
            patchState(store, { job });
            if (config.done(job)) {
              patchState(store, { jobRunning: false });
              forget();
            }
          }),
          catchError((err: unknown) => {
            patchState(store, {
              jobRunning: false,
              jobError: silent ? null : err,
            });
            forget();
            return EMPTY;
          }),
        );

      // Not returned → not part of the store's public surface; `resumeJob` is the door.
      const resumeFrom = rxMethod<string>(
        pipe(switchMap((jobId) => watch(config.poll(jobId), true))),
      );

      return {
        startJob: rxMethod<Input>(
          pipe(
            tap(() => {
              patchState(store, {
                job: null,
                jobError: null,
                jobRunning: true,
              });
            }),
            // switchMap, NOT exhaustMap: re-exporting with a different range must
            // replace the job being watched, not be dropped.
            switchMap((input) => watch(config.start(input), false)),
          ),
        ),
        /** Re-attach to the job this tab left in flight. No-op when there is none. */
        resumeJob(): void {
          const jobId = config.persistKey
            ? sessionStorage.getItem(config.persistKey)
            : null;
          if (!jobId) return;
          patchState(store, { jobError: null, jobRunning: true });
          resumeFrom(jobId);
        },
        clearJob(): void {
          patchState(store, initialState);
          forget();
        },
      };
    }),
  );
}
